import {
  PublicOrderResponse,
  publicOrderResponseSchema,
} from "@redotech/redo-public-api-types-v2/types";
import * as z from "zod";

/**
 * Parsers for the two `get_order` tool output shapes: success and error.
 * Each returns `null` on mismatch so the renderer can fall back to the
 * generic JSON viewer.
 *
 * The success shape is `{ order: PublicOrderResponse }` — the same shape the
 * `get_order` tool returns. We reuse `publicOrderResponseSchema` from
 * `@redotech/redo-public-api-types-v2/types` rather than redeclaring the order
 * fields here. See redo/agents/public/src/orders/get-order.ts.
 */

export const OrderDataErrorSchema = z.object({ error: z.string() });
export type OrderDataError = z.infer<typeof OrderDataErrorSchema>;

export type OrderDoc = PublicOrderResponse;

const SuccessOutputSchema = z
  .object({ order: publicOrderResponseSchema })
  .passthrough();

export function tryParseOrderDoc(output: unknown): OrderDoc | null {
  const result = SuccessOutputSchema.safeParse(output);
  return result.success ? result.data.order : null;
}

export function tryParseOrderError(output: unknown): OrderDataError | null {
  const result = OrderDataErrorSchema.safeParse(output);
  return result.success ? result.data : null;
}

export function formatCustomerName(
  customer: OrderDoc["customer"] | null | undefined,
): string | null {
  const given = (customer?.name?.given ?? "").trim();
  const surname = (customer?.name?.surname ?? "").trim();
  const full = `${given} ${surname}`.trim();
  return full.length > 0 ? full : null;
}

export function totalItemCount(
  items: OrderDoc["items"] | null | undefined,
): number {
  if (!items) {
    return 0;
  }
  return items.reduce((sum, item) => sum + (item.quantity ?? 0), 0);
}

/** Parse a wire money amount (stringified decimal) to a number, or null. */
export function parseAmount(
  money: { amount?: string | null } | null | undefined,
): number | null {
  const raw = money?.amount;
  if (raw == null || raw.trim() === "") {
    return null;
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * The currency to format this order's amounts in. Prefers the order total's
 * currency, then falls back to any line-level currency present.
 */
export function orderCurrency(doc: OrderDoc): string | undefined {
  return (
    doc.total?.currency ??
    doc.lineItemsTotal?.currency ??
    doc.shippingCost?.currency ??
    doc.taxes?.currency ??
    undefined
  );
}
